const HW_UID_STORAGE_KEY = 'ott_hw_uid';
const LGUDID_TIMEOUT_MS = 4_000;

export type PlatformDeviceIdType = 'tizen-duid' | 'webos-lgudid';

export interface DeviceIdentity {
  hardwareUid: string;
  platformDeviceId?: string;
  platformDeviceIdType?: PlatformDeviceIdType;
  platform: 'tizen' | 'webos' | 'web';
}

function clean(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function generateInstallationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
}

async function readLgUdid(): Promise<string | null> {
  const runtime = window as any;
  return new Promise<string | null>((resolve) => {
    let finished = false;
    const finish = (value: unknown) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      resolve(clean(value));
    };
    const timer = setTimeout(() => finish(null), LGUDID_TIMEOUT_MS);

    try {
      if (runtime.webOSDev?.LGUDID) {
        runtime.webOSDev.LGUDID({
          onSuccess: (response: { id?: string }) => finish(response?.id),
          onFailure: () => finish(null),
        });
        return;
      }

      if (runtime.webOS?.service?.request) {
        runtime.webOS.service.request('luna://com.webos.service.sm', {
          method: 'deviceid/getIDs',
          parameters: { idType: ['LGUDID'] },
          onSuccess: (response: { idList?: Array<{ idType?: string; idValue?: string }> }) => {
            const item = response?.idList?.find((entry) => entry.idType === 'LGUDID');
            finish(item?.idValue);
          },
          onFailure: () => finish(null),
        });
        return;
      }
    } catch {
      finish(null);
      return;
    }

    finish(null);
  });
}

export async function getDeviceIdentity(): Promise<DeviceIdentity> {
  try {
    const duid = clean((window as any).webapis?.productinfo?.getDuid?.());
    if (duid) {
      return {
        hardwareUid: duid,
        platformDeviceId: duid,
        platformDeviceIdType: 'tizen-duid',
        platform: 'tizen',
      };
    }
  } catch {
    // Not a Samsung Tizen runtime.
  }

  const isWebOs = Boolean((window as any).webOS || (window as any).webOSDev);
  if (isWebOs) {
    const lgUdid = await readLgUdid();
    if (lgUdid) {
      return {
        hardwareUid: lgUdid,
        platformDeviceId: lgUdid,
        platformDeviceIdType: 'webos-lgudid',
        platform: 'webos',
      };
    }
  }

  let installationId: string | null = null;
  try { installationId = clean(localStorage.getItem(HW_UID_STORAGE_KEY)); } catch { /* ignore */ }
  if (!installationId) {
    installationId = generateInstallationId();
    try { localStorage.setItem(HW_UID_STORAGE_KEY, installationId); } catch { /* ignore */ }
  }
  return {
    hardwareUid: installationId,
    platform: isWebOs ? 'webos' : 'web',
  };
}

export function formatActivationDisplayId(value: string): string {
  const normalized = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return normalized.match(/.{1,3}/g)?.join('.') || normalized;
}
